import { ActionTypes, ProgramTypes } from '../ChannelArchitecture.d';
import { Constants } from '../helper/constants';
import { getMondayOfTheWeek } from 'utils';
import { Internationalization } from '@syncfusion/ej2-base';

const intlInstance = new Internationalization();

/**
 * Determines the selected day of the week object based on the date and modal type.
 *
 * @param {Date|string} date - The start date of the block.
 * @param {string} modalType - The current modal action (e.g., ActionTypes.CREATE).
 * @returns {Object|undefined} The day object from Constants.daysOfWeek (e.g., { name: 'Monday', ... }) or undefined if not found.
 */
const getSelectedDayOfWeek = (date, modalType) => {
  const { daysOfWeek } = Constants;
  const startDay =
    modalType == ActionTypes.CREATE ? getMondayOfTheWeek(new Date()) : date;

  // Get the full name of the day of the week
  const day = intlInstance.formatDate(new Date(startDay), {
    skeleton: 'E',
    type: 'date',
  });
  return daysOfWeek.find((dayOfWeek) => dayOfWeek.name.includes(day));
};

/**
 * Validates and returns the layout input string.
 * Returns a default instructional message if the input is invalid or empty.
 *
 * @param {string} input - The layout value (e.g., 'Sequential', 'Shuffle').
 * @returns {string} The valid layout string or a placeholder message.
 */
const displayLayoutInput = (input) => {
  const layout = ['Sequential', 'Shuffle'];
  if (!input && input?.length) return 'Please select a preferred placement';

  return layout.includes(input) ? input : 'Please select a preferred placement';
};

/**
 * Maps raw block data into the initial form state required for the Edit/Repeat Modal.
 * Handles logic for resolving linked Series, Seasons, and Title Groups, as well
 * as determining which fields should be disabled based on the Program Type.
 *
 * @param {Object} params - The parameter object.
 * @param {Object} params.blockData - The raw data object of the selected block.
 * @param {string} params.modalType - The current action type (e.g., 'REPEAT', 'EDIT').
 * @param {Array<Object>} params.referenceList - List of available block references for linking.
 * @param {Object} params.schedules - The overall schedule object, used to find parent series details for repeated blocks.
 * @param {Array<Object>} params.seriesList - List of available Series for dropdown resolution.
 * @param {Array<Object>} params.titleGroupList - List of available Title Groups for dropdown resolution.
 *
 * @returns {Object|null} A state object populated with form values and 'disable' flags, or null if blockData is missing.
 */
const getEditBlockState = ({
  blockData,
  modalType,
  referenceList,
  schedules,
  seriesList,
  titleGroupList,
}) => {
  if (!blockData || Object.keys(blockData).length === 0) return null;

  const {
    StartTime,
    EndTime,
    IsSequential,
    IsLinked,
    BlockReference,
    TitleGroupName,
    BlockName,
    Genre,
    MaxCount,
    AvailableDuration,
    Layout,
    SeriesName,
    Type,
    SeasonID,
  } = blockData;

  const isModalSingleRepeatType = modalType === ActionTypes.REPEAT;
  const isModalMultipleRepeatType = modalType === ActionTypes.MULTIPLEREPEAT;
  const isModalOfRepeatType = isModalSingleRepeatType || isModalMultipleRepeatType;
  const isProgramRepeat = Type === ProgramTypes.REPEAT;
  const selectedTitleGroup = titleGroupList?.find(
    (o) => o.name === TitleGroupName
  );

  // Find Link References
  // If Repeat modal, look up by BlockName; otherwise by BlockReference
  const getReference = (key) =>
    referenceList?.find((item) => item.blockName === key) || null;
  const repeatLinkReference = getReference(BlockName);
  const standardLinkReference = getReference(BlockReference);
  const activeLinkRef = (isModalOfRepeatType)
    ? repeatLinkReference
    : IsLinked
    ? standardLinkReference
    : null;

  // Find Series
  let targetSeriesName = SeriesName;

  // If it's a REPEAT type, we might need to grab the Series Name from the referenced block
  if (isProgramRepeat && standardLinkReference) {
    const referenceBlock = schedules.programmes.find(
      (e) => standardLinkReference.blockName === e.blockName
    );
    if (referenceBlock?.seriesName) {
      targetSeriesName = referenceBlock.seriesName;
    }
  }

  const selectedSeries = seriesList?.find((o) => o.name === targetSeriesName);

  // Find Season
  const selectedSeason =
    selectedSeries && SeasonID
      ? selectedSeries.seasons.find((o) => o.id === +SeasonID)
      : null;

  // Build Shared Base Values
  // These values are needed for BOTH Standard Edit and Repeat Edit
  const commonValues = {
    // Note: 'type' might be overwritten below for Repeat modal
    type: Type === 'TitleGroup' ? ProgramTypes.TITLEGROUP : Type,
    weekNumber: 1,
    dayOfWeek: getSelectedDayOfWeek(StartTime, modalType),
    sequential: IsSequential,
    series: selectedSeries,
    seasons: selectedSeason,
    genre: Genre,
    blockName: BlockName,
    layout: displayLayoutInput(Layout),
    programMaxCount: MaxCount,
    programAvailableDuration: AvailableDuration,
    titleGroup: selectedTitleGroup,
    linkReference: activeLinkRef,
  };

  // Repeat Edit Mode
  if (isModalOfRepeatType) {
    return {
      ...commonValues,
      // Overwrites specific to Repeat Mode
      type: ProgramTypes.REPEAT,
      hasLink: true,
      startTime: null,
      endTime: null,

      // UI Booleans
      disableType: true,
      disableSeason: true,
      disableSeries: true,
      disablePlacement: true,
      disableTitleGroup: true,
      disableLinkReference: true,
      disableLinkToggle: true,
      disableEndTime: false,
      disableStartTime: false,
      disableReccurrence: isModalMultipleRepeatType,
    };
  }

  // Standard Edit Mode
  const isTitleGroup =
    Type === 'TitleGroup' || Type === ProgramTypes.TITLEGROUP;
  const isSeries = Type !== ProgramTypes.SERIES;

  return {
    ...commonValues,
    startTime: StartTime,
    endTime: EndTime,
    hasLink: IsLinked,

    // UI Booleans
    disableSeries: isSeries || isProgramRepeat,
    disableSeason: isSeries || isProgramRepeat,
    disableTitleGroup: !isTitleGroup || isProgramRepeat,
    disableType: isProgramRepeat,
    disableLinkReference: isProgramRepeat,
    disablePlacement: isProgramRepeat,
    disableLinkToggle: isProgramRepeat,
    disableEndTime: false,
    disableStartTime: false,
    disableDayOfWeek: true,
  };
};

export default getEditBlockState;
